import React, { useEffect, useMemo, useRef } from "react";
import type { GameFrames, StandingsRow } from "../api/types";
import { formatUserHandle } from "../../../utils/userHandle";
import { rankLabel } from "../utils/seasonText";
import { GAME_BOARD_LABELS, PROVISIONAL_NOTE, bracketPieces, framesProvisional, rowFrameKind } from "../utils/frames";
import { FrameArea, FrameBracket, FrameLegend, frameRowFill } from "./FrameMarks";

interface Props {
  rows: StandingsRow[];
  /** Who takes an AOTD IRL frame; missing on an older backend. */
  frames?: GameFrames | null;
  /** Scroll my row into view on small screens (once). */
  scrollToMe?: boolean;
}

function RankChange({ change }: { change: number | null }) {
  if (change == null || change === 0) return <span className="gm-standings__change" />;
  const up = change > 0;
  return (
    <span
      className={`gm-standings__change ${up ? "gm-standings__change--up" : "gm-standings__change--down"}`}
      aria-label={up ? `up ${change}` : `down ${Math.abs(change)}`}
    >
      {up ? "▲" : "▼"} {Math.abs(change)}
    </span>
  );
}

/**
 * Season standings: rank (shared ranks print as "=2"), handle, total, today's
 * points and the move since the previous day. A list of grid rows rather than a
 * table, so it can stack under 480 px.
 */
const StandingsTable: React.FC<Props> = ({ rows, frames = null, scrollToMe = true }) => {
  const meRef = useRef<HTMLLIElement | null>(null);
  const scrolled = useRef(false);

  const sharedRanks = useMemo(() => {
    const counts = new Map<number, number>();
    rows.forEach((r) => {
      if (r.rank != null) counts.set(r.rank, (counts.get(r.rank) ?? 0) + 1);
    });
    return counts;
  }, [rows]);

  useEffect(() => {
    if (!scrollToMe || scrolled.current || !meRef.current) return;
    scrolled.current = true;
    if (typeof window !== "undefined" && window.innerWidth < 640) {
      meRef.current.scrollIntoView?.({ block: "center", behavior: "smooth" });
    }
  }, [rows, scrollToMe]);

  const kinds = rows.map((r) => rowFrameKind(r, frames));
  const pieces = bracketPieces(kinds, GAME_BOARD_LABELS);
  const marked = kinds.some(Boolean);

  if (rows.length === 0) return <p className="gm-muted">No players yet.</p>;

  return (
    <>
    <FrameArea $marked={marked} $compact>
    <ol className="gm-standings" aria-label="Standings">
      <li className="gm-standings__row gm-standings__row--head" aria-hidden>
        <span>#</span>
        <span>Player</span>
        <span className="gm-standings__total">Pts</span>
        <span className="gm-standings__today">Today</span>
        <span className="gm-standings__change">Move</span>
      </li>
      {rows.map((r, i) => {
        const piece = pieces[i];
        const shared = r.rank != null && (sharedRanks.get(r.rank) ?? 0) > 1;
        const today = r.today && r.today.total > 0 ? `+${r.today.total}` : "";
        return (
          <li
            key={r.username}
            ref={r.me ? meRef : undefined}
            className={`gm-standings__row${r.me ? " gm-is-me" : ""}${piece ? " gm-standings__row--frame" : ""}`}
            data-testid={`standings-row-${r.username}`}
            data-frame={piece?.kind}
            aria-current={r.me ? "true" : undefined}
            style={{ backgroundColor: frameRowFill(piece?.kind) }}
          >
            <span className="gm-standings__rank">{rankLabel(r.rank, shared)}</span>
            {piece ? <FrameBracket piece={piece} srText={GAME_BOARD_LABELS[piece.kind]} /> : null}
            <span className="gm-standings__handle">{formatUserHandle(r.username, r.emoji)}</span>
            <span className="gm-standings__total">{r.total}</span>
            <span className="gm-standings__meta">
              <span className="gm-standings__today" aria-label={today ? `${today} today` : undefined}>
                {today}
              </span>
              <RankChange change={r.rankChange} />
            </span>
          </li>
        );
      })}
    </ol>
    <FrameLegend kinds={kinds} labels={GAME_BOARD_LABELS} style={{ marginTop: 10, marginBottom: 0 }} />
    </FrameArea>
    {marked && framesProvisional(frames) ? <p className="gm-muted gm-standings__note">{PROVISIONAL_NOTE}</p> : null}
    </>
  );
};

export default StandingsTable;
